import { ChangeDetectorRef, Component, Inject, Input, OnInit } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
  MatLegacyDialog
} from "@angular/material/legacy-dialog";
import { Router } from '@angular/router';
import { CtButtonConfiguration, CtGenericDialogComponent, CtGenericDialogConfiguration, MAT_RAISED_PRIMARY } from '@ctsolution/ct-framework';
import { CtWebapiGenericResponse, CtWebapiService, DataRequest, MethodEnum } from '@ctsolution/ct-webapi';
import { lastValueFrom } from 'rxjs';

/**
 * Componente per la duplicazione di un Task/Progetto esistente.
 * 
 * Funzionalità:
 * - Mostra un pulsante "Duplica" con icona content_copy
 * - Al click, apre un dialog di conferma
 * - Se confermato, chiama l'API /task/Clone passando il taskOid
 * - Naviga alla pagina di edit del nuovo task clonato
 * 
 * Pattern replicato da work-order-clone.component.ts
 * 
 * @example
 * <app-task-clone [pid]="taskOid"></app-task-clone>
 */
@Component({
  selector: 'app-task-clone',
  templateUrl: './task-clone.component.html',
  styleUrls: ['./task-clone.component.scss']
})
export class TaskCloneComponent implements OnInit {

  /** OID del task da clonare */
  @Input() pid: number | null = null;

  /** Configurazione del pulsante "Duplica" */
  cloneConfiguration: CtButtonConfiguration = CtButtonConfiguration.create()
    .setLabel("Duplica")
    .setIcon("content_copy")
    .setMatherialOptions(MAT_RAISED_PRIMARY)
    .setAction(() => this.open())

  constructor(
    public dialogRef: MatDialogRef<TaskCloneComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private router: Router,
    private dialog: MatLegacyDialog,
    private cdr: ChangeDetectorRef,
    private _webapi: CtWebapiService
  ) { }

  ngOnInit() {
  }

  /**
   * Apre il dialog di conferma e gestisce il flusso di clonazione
   * 
   * Flow:
   * 1. Mostra dialog di conferma
   * 2. Se confermato, chiama l'API di clonazione
   * 3. Naviga alla pagina di edit del nuovo task
   */
  async open() {

    const dialogConfig = this.createDialogConfig();
    const res = await lastValueFrom(this.dialog.open(CtGenericDialogComponent, { data: { configuration: dialogConfig } }).afterClosed());

    // Se l'utente annulla o non c'è un pid valido, esce
    if (!res || !this.pid) return;

    try {
      const result = await this.cloneTask();
      if (result) {
        this.navigateToTaskEdit(result);
      }
    } catch (error) {
      console.error("Errore durante la clonazione del Task:", error);
    }
  }

  /**
   * Crea la configurazione per il dialog di conferma
   * 
   * @returns Configurazione del dialog con titolo, contenuto e label dei pulsanti
   */
  private createDialogConfig(): CtGenericDialogConfiguration {
    return CtGenericDialogConfiguration.create()
      .setTitle("taskClone")
      .setContent("taskCloneContent")
      .setSubmitLabel("Duplica")
      .setCancelLabel("Annulla");
  }

  /**
   * Chiama l'API per clonare il Task
   * 
   * Endpoint: POST /task/Clone
   * Body: { taskOid: number }
   * 
   * @returns Promise con l'OID del nuovo task clonato
   */
  private async cloneTask(): Promise<any> {
    const request = DataRequest.create()
      .setController(["task"])
      .setAction("Clone")
      .setBody({ taskOid: this.pid })
      .setMethod(MethodEnum.POST);

    const response: CtWebapiGenericResponse<any> = await lastValueFrom(this._webapi.request(request));
    return response?.Result;
  }

  /**
   * Naviga alla pagina di modifica del Task clonato
   * 
   * Utilizza skipLocationChange per forzare il reload della pagina
   * e garantire che tutti i dati vengano ricaricati correttamente
   * 
   * @param taskOid - OID del task clonato da aprire in edit
   */
  private navigateToTaskEdit(taskOid: any) {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate(['/', 'task', 'edit', taskOid]);
    });
  }

}
